import type { DriverDocumentKey } from "../types/vtc";
import type { CompanyDocStatus } from "../types/company";
import { DRIVER_DOCUMENTS } from "../data/auth";
import { api } from "./api";

/** A driver document as the driver area shows it. */
export interface DriverDocument {
  key: DriverDocumentKey;
  status: CompanyDocStatus; // "missing" when not uploaded yet
  fileName?: string;
  uploadedAt?: string;
  reviewNote?: string;
}

export interface DriverProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  language?: "en" | "fr";
  address?: string;
  city?: string;
  postalCode?: string;
  region?: string;
  country?: string;
  licenceNumber?: string;
  licenceDate?: string;
  vtcCardNumber?: string;
  /** VTC card expiry, "YYYY-MM-DD…" */
  vtcCardExpiry?: string;
  status: "pending" | "verified" | "rejected" | "suspended";
  documents: DriverDocument[];
}

interface ApiDriver extends Omit<DriverProfile, "documents"> {
  documents?: Partial<
    Record<
      DriverDocumentKey,
      { status: Exclude<CompanyDocStatus, "missing">; originalName?: string; uploadedAt?: string; reviewNote?: string }
    >
  >;
}

const toProfile = ({ documents, ...driver }: ApiDriver): DriverProfile => ({
  ...driver,
  documents: DRIVER_DOCUMENTS.map((key) => {
    const doc = documents?.[key];
    return doc
      ? { key, status: doc.status, fileName: doc.originalName, uploadedAt: doc.uploadedAt, reviewNote: doc.reviewNote }
      : { key, status: "missing" };
  }),
});

/** GET /driver/profile */
export async function getDriverProfile(): Promise<DriverProfile> {
  const { data } = await api.get<ApiDriver>("/driver/profile");
  return toProfile(data);
}

/**
 * GET /driver/documents/:type — the route needs the token, so the file is
 * fetched as a blob and opened in a new tab. The tab is opened first (in the
 * click) so popup blockers allow it.
 */
export async function openDriverDocument(key: DriverDocumentKey): Promise<void> {
  const tab = window.open("", "_blank");
  try {
    const { data } = await api.get<Blob>(`/driver/documents/${key}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    if (tab) tab.location.href = url;
    else window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/** PUT /driver/documents — uploads or replaces one document. */
export async function uploadDriverDocument(key: DriverDocumentKey, file: File): Promise<string> {
  const form = new FormData();
  form.append(key, file);
  const { data } = await api.put<{ message: string }>("/driver/documents", form);
  return data.message;
}

/* ---------- Profile ---------- */

export type DriverProfileUpdate = Partial<
  Pick<DriverProfile, "firstName" | "lastName" | "phone" | "address" | "city" | "postalCode" | "language" | "vtcCardExpiry">
>;

/** PUT /driver/profile */
export async function updateDriverProfile(changes: DriverProfileUpdate): Promise<{ profile: DriverProfile; message: string }> {
  const { data } = await api.put<{ message: string; driver: ApiDriver }>("/driver/profile", changes);
  return { profile: toProfile(data.driver), message: data.message };
}

/* ---------- Bookings and messages ---------- */

export type DriverBookingStatus =
  | "pending"
  | "documents_required"
  | "confirmed"
  | "active"
  | "completed"
  | "declined"
  | "cancelled"
  | "expired";

export interface DriverBooking {
  id: string;
  ref: string;
  status: DriverBookingStatus;
  start: string;
  end: string;
  days: number;
  note: string;
  terms: { daily?: number; weekly: number; monthly: number; deposit: number; kmIncluded: number; minDays?: number; estimate?: number };
  declineReason: string;
  createdAt: string;
  updatedAt: string;
  listing: {
    id: string;
    brand: string;
    model: string;
    version: string;
    city: string;
    photo: string | null;
    /** Only sent once the request is accepted. */
    pickupAddress?: string;
  } | null;
  vehicle: { id: string; code: string; plate: string } | null;
  /** Contact details only once the booking is confirmed. */
  company: { publicId: number | null; name: string; phone?: string; email?: string; address?: string } | null;
  lastMessage: { from: "company" | "driver" | "system"; text: string; at: string } | null;
  unread: number;
  messages?: { id: string; from: "company" | "driver" | "system"; text: string; at: string }[];
  history?: { status: DriverBookingStatus; by: string; at: string; note?: string }[];
}

export interface DriverSummary {
  pendingRequests: number;
  confirmedOrActive: number;
  unreadMessages: number;
  approvedDocuments: number;
  totalDocuments: number;
  documentsToFix: number;
  favourites: number;
  reviewsToWrite: number;
}

/** GET /driver/bookings (full = with messages and history; messages = conversations only) */
export async function getDriverBookings(opts: { full?: boolean; messages?: boolean } = {}): Promise<DriverBooking[]> {
  const params: Record<string, string> = {};
  if (opts.full) params.full = "true";
  if (opts.messages) params.messages = "true";
  const { data } = await api.get<{ items: DriverBooking[] }>("/driver/bookings", { params });
  return data.items;
}

/** GET /driver/bookings/:ref — also marks the company's messages as read. */
export async function getDriverBooking(ref: string): Promise<DriverBooking> {
  const { data } = await api.get<DriverBooking>(`/driver/bookings/${encodeURIComponent(ref)}`);
  return data;
}

/** POST /driver/bookings */
export async function requestBooking(input: { listingId: string; start: string; end: string; note?: string }): Promise<{ booking: DriverBooking; message: string }> {
  const { data } = await api.post<{ booking: DriverBooking; message: string }>("/driver/bookings", input);
  return data;
}

/** POST /driver/bookings/:ref/messages */
export async function sendDriverMessage(ref: string, text: string): Promise<DriverBooking> {
  const { data } = await api.post<DriverBooking>(`/driver/bookings/${encodeURIComponent(ref)}/messages`, { text });
  return data;
}

/** POST /driver/bookings/:ref/cancel */
export async function cancelDriverBooking(ref: string): Promise<DriverBooking> {
  const { data } = await api.post<DriverBooking>(`/driver/bookings/${encodeURIComponent(ref)}/cancel`, {});
  return data;
}

/** GET /driver/summary — dashboard numbers and sidebar badges */
export async function getDriverSummary(): Promise<DriverSummary> {
  const { data } = await api.get<DriverSummary>("/driver/summary");
  return data;
}

/* ---------- Favourites ---------- */

/** GET /driver/favourites — marketplace cards (raw API shape, mapped by marketplaceApi) */
export async function getFavouriteIds(): Promise<string[]> {
  const { data } = await api.get<{ items: { id: string }[] }>("/driver/favourites");
  return data.items.map((c) => c.id);
}

/** PUT / DELETE /driver/favourites/:listingId */
export async function setFavouriteListing(listingId: string, on: boolean): Promise<string[]> {
  const { data } = await api.request<{ favourites: string[] }>({
    url: `/driver/favourites/${encodeURIComponent(listingId)}`,
    method: on ? "PUT" : "DELETE",
  });
  return data.favourites;
}
